import { useEffect } from 'react';

import { usePublicBrand } from '@/platform/brand/use-public-brand';

const ACCENT_FILL_VARS = ['--primary', '--ring', '--sidebar-primary', '--sidebar-ring'] as const;
const ACCENT_TEXT_VARS = ['--primary-foreground', '--sidebar-primary-foreground'] as const;
const DARK_ON_ACCENT = '#0f2740';
const LIGHT_ON_ACCENT = '#ffffff';

function channel(hex: string, offset: number): number {
  return Number.parseInt(hex.slice(offset, offset + 2), 16);
}

function luminance(hex: string): number {
  const linear = (value: number) => {
    const unit = value / 255;
    return unit <= 0.04045 ? unit / 12.92 : ((unit + 0.055) / 1.055) ** 2.4;
  };
  return (
    0.2126 * linear(channel(hex, 1)) +
    0.7152 * linear(channel(hex, 3)) +
    0.0722 * linear(channel(hex, 5))
  );
}

function contrast(foreground: string, background: string): number {
  const lighter = Math.max(luminance(foreground), luminance(background));
  const darker = Math.min(luminance(foreground), luminance(background));
  return (lighter + 0.05) / (darker + 0.05);
}

/** Picks the label color that stays readable on a tenant accent. */
export function foregroundForAccent(hex: string): string {
  return contrast(LIGHT_ON_ACCENT, hex) >= contrast(DARK_ON_ACCENT, hex)
    ? LIGHT_ON_ACCENT
    : DARK_ON_ACCENT;
}

export function applyBrandAccent(root: HTMLElement, accent: string): void {
  const hex = accent.trim();
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) {
    clearBrandAccent(root);
    return;
  }
  const foreground = foregroundForAccent(hex);
  for (const name of ACCENT_FILL_VARS) {
    root.style.setProperty(name, hex);
  }
  for (const name of ACCENT_TEXT_VARS) {
    root.style.setProperty(name, foreground);
  }
}

export function clearBrandAccent(root: HTMLElement): void {
  for (const name of [...ACCENT_FILL_VARS, ...ACCENT_TEXT_VARS]) {
    root.style.removeProperty(name);
  }
}

/** Applies the host accent after the appearance tokens. Empty clears the inline override. */
export function BrandAccentEffect() {
  const brand = usePublicBrand();
  const accent = brand.data?.accentHex?.trim() ?? '';

  useEffect(() => {
    const root = document.documentElement;
    if (!accent) {
      clearBrandAccent(root);
      return;
    }
    applyBrandAccent(root, accent);
    return () => clearBrandAccent(root);
  }, [accent]);

  return null;
}
